import React from 'react';
import { Space, Typography, Grid } from '@arco-design/web-react';
import { t } from '@bay-email/core';

import { CriteriaConfig, getCriteriaFilterSummary } from './criteriaFilterSummary';

interface CriteriaStateProps {
  criteriaConfig: CriteriaConfig;
  resultCount: number;
  maxListings: number;
  loading?: boolean;
  isDesignAdmin?: boolean;
}

export const CriteriaState: React.FC<CriteriaStateProps> = ({
  criteriaConfig,
  resultCount,
  maxListings,
  loading = false,
  isDesignAdmin = false,
}) => {
  const getFilterSummary = React.useMemo(() => getCriteriaFilterSummary(criteriaConfig), [criteriaConfig]);

  const filterCount = getFilterSummary.length;
  const isOverLimit = resultCount > maxListings;
  // Show the actual number that will be displayed: max if over limit, otherwise result count
  const displayCount = isOverLimit ? maxListings : resultCount;

  if (filterCount === 0) {
    // Hide the helper text when in Design Manager mode
    if (isDesignAdmin) {
      return null;
    }

    return (
      <div className="p-3 mt-3 bg-50 text-center text-500 rounded-1">
        {t('No criteria chosen.')}<br />
        {t('Please click "Select Criteria" to get started.')}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Top element - Criteria count */}
      <div style={{ padding: '12px 16px', backgroundColor: '#f7f8fa', borderRadius: '4px' }}>
        <Space align='center'>
          <Typography.Text style={{ fontSize: '18px', fontWeight: 600 }}>
            {filterCount}
          </Typography.Text>
          <Typography.Text style={{ fontWeight: 500, color: '#4E5969' }}>
            {filterCount === 1 ? t('Filter Active') : t('Filters Active')}
          </Typography.Text>
        </Space>
      </div>

      {/* Bottom two INLINE elements - Results count and Max count side by side */}
      <div style={{ display: 'flex', gap: '12px' }}>
        {/* Results count */}
        <div style={{ flex: 1, padding: '12px 16px', backgroundColor: '#f7f8fa', borderRadius: '4px' }}>
          <Typography.Text
            type='secondary'
            style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}
          >
            {t('Results Found')}
          </Typography.Text>
          <Typography.Text style={{ fontSize: '16px', fontWeight: 600 }}>
            {loading
              ? t('Loading...')
              : `${resultCount.toLocaleString()}`}
          </Typography.Text>
          <Typography.Text style={{ fontSize: '14px', color: '#86909c', marginLeft: '4px' }}>
            {resultCount === 1 ? t('listing') : t('listings')}
          </Typography.Text>
        </div>

        {/* Max count */}
        <div style={{ flex: 1, padding: '12px 16px', backgroundColor: '#f7f8fa', borderRadius: '4px' }}>
          <Typography.Text
            type='secondary'
            style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}
          >
            {t('Max Limit')}
          </Typography.Text>
          <Typography.Text style={{ fontSize: '16px', fontWeight: 600 }}>
            {maxListings}
          </Typography.Text>
          <Typography.Text style={{ fontSize: '14px', color: '#86909c', marginLeft: '4px' }}>
            {maxListings === 1 ? t('listing') : t('listings')}
          </Typography.Text>
        </div>
      </div>

      {/* Warning if over limit */}
      {isOverLimit && (
        <div
          style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: '4px',
          }}
        >
          <Typography.Text style={{ fontSize: '12px', color: '#d48806' }}>
            ⚠️ {t('Showing')} <strong>{maxListings}</strong> {t('of')} <strong>{resultCount.toLocaleString()}</strong> {t('total results')}
          </Typography.Text>
        </div>
      )}
    </div>
  );
};
